<template>
  <!-- Upload Preview -->
  <div
    v-if="uploadedPreviewData?.id"
    class="p-2 grid border-round shadown-inset-2"
    data-cy="deviation-survey-preview"
  >
    <div
      class="col-12 md:col-7 surface-300 border-round shadow-inset-2 p-4 m-4"
      data-cy="deviation-survey-preview-card"
    >
      <div class="flex align-items-center justify-content-between mb-3 body-2">
        <span data-cy="deviation-survey-preview-title">
          <icon :icon="['fas', 'file-check']" class="text-gray-700" />
          <span class="ml-1 text-gray-800">
            {{ uploadedPreviewData?.title }}
          </span>
        </span>
        <span
          class="flex align-items-center text-gray-700"
          data-cy="deviation-survey-preview-button"
        >
          <icon :icon="['far', 'eye']" />
          <span class="ml-1">Preview</span>
        </span>
      </div>
      <DeviationSurveyTable
        :rowHeight="38"
        :survey-data="uploadedPreviewData.entry_rows"
        :survey-direction-type="uploadedPreviewData.directional_data_set"
        :is-preview="true"
        :depth-unit="sizingUnit"
        data-cy="deviation-survey-preview-table"
      />
    </div>
    <div
      class="col flex flex-column align-items-center justify-content-between"
      data-cy="deviation-survey-actions"
    >
      <div class="flex flex-column p-4">
        <div class="flex text-gray-800 mb-4">
          <icon :icon="['fas', 'exclamation-circle']" />
          <span class="ml-2">
            To display deviation survey, select directional data set used in
            this file.
          </span>
        </div>

        <FormLabel
          v-slot="{ id }"
          text="Directional Data Set"
          class="w-full my-2"
          data-cy="deviation-survey-directional-data-set-label"
        >
          <Dropdown
            :id="id"
            v-model="uploadedPreviewData.directional_data_set"
            :options="directionalDataSets?.deviation_survey_directional_dataset"
            class="w-full"
            option-value="value"
            option-label="comment"
            append-to="body"
            data-cy="deviation-survey-directional-data-set-dropdown"
            @change="dropDownChange"
          />
        </FormLabel>

        <UndoConfirmButtons
          class="col-12 mt-4 text-right"
          undo-label="Discard"
          confirm-label="Complete Upload"
          :confirm-disabled="
            !uploadedPreviewData.directional_data_set ||
            !uploadedPreviewData.title ||
            !uploadedPreviewData.entry_rows?.length
          "
          data-cy="deviation-survey-undo-confirm-buttons"
          @undo="clearPreviewForm"
          @confirm="onAccept"
        />
      </div>
    </div>
  </div>
  <!-- No Deviation Survey (Empty) -->
  <div v-else class="flex align-items-center" data-cy="deviation-survey-empty">
    <div class="flex justify-content-between align-items-center">
      <div>
        <FileInput
          icon="fas fa-plus-circle"
          label="Add Survey Sheet"
          :auto="true"
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          :maxFileSize="5000000"
          :show-cancel-button="true"
          data-cy="deviation-survey-add-survey-sheet"
          @change="fileInputChange"
        />
      </div>
      <span class="body-1 text-gray-800 ml-4"> No Survey Added </span>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * Xlsx mini build is untyped so ts-ignore is added to satisfy compiler
 * Worksheet type is imported and manually added to the read function result
 * https://github.com/SheetJS/sheetjs/issues/1999
 *
 * Mini build is used because it is 212KB vs 1.2MB
 * for the whole build
 */
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import { read } from "xlsx/dist/xlsx.mini.min.js";
import type { CellObject, WorkSheet } from "xlsx";

import type { PropType } from "vue";
import { ref } from "vue";
import { v4 } from "uuid";
import type { Deviation_Survey } from "gql/api";
import {
  Deviation_Survey_Directional_Dataset_Enum,
  useDirectionalDataSetQuery
} from "gql/api";
import { LengthUnits } from "@/libs/units/length";
import FormLabel from "@/components/FormLabel.vue";
import FileInput from "@/components/FileInput.vue";
import UndoConfirmButtons from "@/components/forms/UndoConfirmButtons.vue";
import DeviationSurveyTable from "./DeviationSurveyTable.vue";
import { injectStrict } from "@/utils/utils";
import {
  deviationSurveyFormKey,
  wellPropertiesFormKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import {
  calculateDeviationSurveyRowData,
  extrapolateTVDByMD
} from "@/libs/calculations/deviationSurvey";
import type { ConvertableUnit } from "@/libs/units";
import { useToast } from "primevue/usetoast";
import type { DeviationSurveyEntryRows } from "@/composables/scenarioCalculations/calculationTypes";

defineProps({
  sizingUnit: {
    type: Object as PropType<ConvertableUnit>,
    default: LengthUnits.Feet
  }
});

const uploadedFile = ref();
const wellPropertiesForm = injectStrict(wellPropertiesFormKey);
const deviationSurveyForm = injectStrict(deviationSurveyFormKey);

const { data: directionalDataSets } = useDirectionalDataSetQuery();

const uploadedPreviewData = ref<Partial<Deviation_Survey>>({
  directional_data_set:
    Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth,
  entry_rows: []
});

const toaster = useToast();

async function parseDeviationSurveyEntryRows(
  xlsxFile: File
): Promise<number[][]> {
  const buffer = await xlsxFile.arrayBuffer();
  const allCells: WorkSheet = read(buffer).Sheets.Sheet1;
  let parseError = false;
  let depthUnit = LengthUnits.Feet;

  const deviationSurveyRowEntries: DeviationSurveyEntryRows = {
    data: [],
    surveyType: uploadedPreviewData.value.directional_data_set
      ? uploadedPreviewData.value.directional_data_set
      : null
  };

  if (!allCells) {
    toaster.add({
      severity: "error",
      life: 4000,
      summary: "Deviation Survey Upload Error",
      detail: `Datasheet not formatted properly`
    });
    parseError = true;
  } else {
    // This cell inside the spreadsheet tells us what unit the values are
    if (allCells?.A8.v === "(meters)") {
      depthUnit = LengthUnits.Meters;
    }

    const lastRowOfColumnA = Math.max(
      ...Object.keys(allCells)
        .filter(cellIndex => cellIndex.startsWith("A"))
        .map(cellIndex => Number(cellIndex.slice(1)))
    );

    // Build the rows array
    const rows: Array<{
      rowNum: number;
      colA: CellObject;
      colB: CellObject;
      colC?: CellObject;
    }> = [];

    for (let row = 9; row <= lastRowOfColumnA; row++) {
      const colA = allCells["A" + row];
      const colB = allCells["B" + row];
      const colC = allCells["C" + row];

      rows.push({ rowNum: row, colA, colB, colC });
    }

    // Check if the first row of data is not all zeros
    const firstRowData = rows[0];
    const firstColAValue = firstRowData.colA?.v ?? 0;
    const firstColBValue = firstRowData.colB?.v ?? 0;
    const firstColCValue = firstRowData.colC?.v ?? 0;

    if (firstColAValue !== 0 || firstColBValue !== 0 || firstColCValue !== 0) {
      // Insert a zeroed-out row at the beginning
      rows.unshift({
        rowNum: 8, // Assuming row number 8
        colA: { t: "n", v: 0 },
        colB: { t: "n", v: 0 },
        colC: { t: "n", v: 0 }
      });
    }

    // Now process the rows
    for (let i = 0; i < rows.length; i++) {
      const currentRow = rows[i];
      const rowNumber = currentRow.rowNum;
      const colA = currentRow.colA;
      const colB = currentRow.colB;
      const colC = currentRow.colC;

      if (colA?.t === "n" && colB?.t === "n") {
        if (i > 0) {
          const prevColA = rows[i - 1].colA;
          // MDs must be sequentially increasing
          if ((prevColA?.v ?? 0) > (colA?.v ?? 0)) {
            toaster.add({
              severity: "error",
              life: 4000,
              summary: "Deviation Survey Upload Error",
              detail: `Non-sequential increasing measured depth provided in row ${rowNumber}`
            });
            parseError = true;
            break;
          }
        }

        deviationSurveyRowEntries.data.push([
          Number(colA.v),
          Number(colB.v),
          Number(colC?.v ?? 0)
        ]);
      } else {
        toaster.add({
          severity: "error",
          life: 4000,
          summary: "Deviation Survey Upload Error",
          detail: `Bad data given in row ${rowNumber}`
        });
        parseError = true;
        break;
      }
    }
  }

  return parseError
    ? []
    : calculateDeviationSurveyRowData(deviationSurveyRowEntries, depthUnit);
}

async function dropDownChange() {
  const entry_rows = await parseDeviationSurveyEntryRows(uploadedFile.value);
  if (entry_rows.length > 0) {
    uploadedPreviewData.value = {
      ...uploadedPreviewData.value,
      id: v4(),
      title: uploadedFile.value.name,
      entry_rows
    };
  }
}

// Triggered by FileInput and handle upload of a CSV file.
// Parses CSV and stores result in ref
async function fileInputChange(event: Event) {
  const { files } = event.target as HTMLInputElement;

  const handleError = (detail: string) => {
    toaster.add({
      severity: "error",
      life: 4000,
      summary: "Deviation Survey Upload Error",
      detail
    });
  };

  if (!files || !files[0]) {
    return handleError("No file selected");
  }

  const file: File = files[0];

  uploadedFile.value = file;

  const entry_rows = await parseDeviationSurveyEntryRows(file);
  if (!entry_rows.length) {
    return handleError("No row entries found in uploaded deviation survey.");
  }

  uploadedPreviewData.value = {
    ...uploadedPreviewData.value,
    id: v4(),
    title: file.name,
    entry_rows
  };
}

// Completes the upload of a new deviation survey
// by updating the form state with the new uploaded data
function onAccept() {
  // Update wellProperties form state to include new
  // deviation survey id.
  wellPropertiesForm.update({
    deviation_survey_id: uploadedPreviewData.value?.id
  });
  // Update Deviation Survey form state
  deviationSurveyForm.update(
    Object.assign(uploadedPreviewData.value, {
      entry_rows: uploadedPreviewData.value.entry_rows,
      deleted_at: null
    })
  );
  // Update top of perfs tvd with survey estimation
  const rowData: DeviationSurveyEntryRows = {
    surveyType: uploadedPreviewData.value.directional_data_set,
    data: uploadedPreviewData.value.entry_rows || []
  };
  wellPropertiesForm.update({
    average_dls:
      rowData.surveyType ===
      Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
        ? null
        : wellPropertiesForm.data.value.average_dls,
    top_of_perforations_true_vertical_depth_feet: extrapolateTVDByMD(
      wellPropertiesForm.data.value.top_of_perforations_measured_depth_feet,
      rowData
    )
  });
  // Clear state of uploaded CSV because data is
  // now stored in the form state.
  clearPreviewForm();
}

function clearPreviewForm() {
  uploadedPreviewData.value = {
    directional_data_set:
      Deviation_Survey_Directional_Dataset_Enum.ThreeDMdInclinationAzimuth
  };
}
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 928px) {
  .mobile-devi {
    justify-content: center;
  }
  .body-2 {
    font-size: 12px;
  }
}
</style>
